import { count, kwh } from "../../lib/format";
import type { UsageTotals } from "../../lib/types";
import { Cell } from "../ui/Table";

/**
 * One row's energy in the selected window — the fleet table's column and the
 * depot table's, which is why it lives here rather than in either page.
 *
 * The session count sits under the figure and is not optional decoration. kWh
 * alone cannot tell the two interesting zeroes apart: a station that ran forty
 * sessions and delivered nothing is broken, and one that ran none was simply not
 * used. An operator scanning this column is looking for exactly that difference.
 *
 * `usage` is never `null` here — the column only renders when a window is
 * selected, and the server fills every row in that case — but the guard stays,
 * because a table that throws on an unexpected `null` takes the whole page with
 * it, and the honest fallback is a dash.
 */
export function EnergyCell({ usage }: { readonly usage: UsageTotals | null }) {
  if (usage === null) {
    return (
      <Cell numeric muted>
        —
      </Cell>
    );
  }

  const idle = usage.closedSessionCount === 0;

  return (
    <Cell numeric muted={idle}>
      <div className="flex flex-col items-end gap-0.5">
        <span className={idle ? "" : "font-medium text-ink"}>
          {kwh(usage.energyWh)}
          <span className="ml-1 text-micro tracking-normal normal-case text-ink-faint">kWh</span>
        </span>
        <span className="text-micro tracking-normal normal-case text-ink-faint">
          {idle ? "no sessions" : `${count(usage.closedSessionCount)} closed`}
        </span>
      </div>
    </Cell>
  );
}
